import React, {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { Form, FormRenderProps, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Tabs, Tab } from '@mui/material';
import i18next from 'i18next';

import { FormContent, FormButtonContainer, FormSubmitButton } from 'styles/form';

import { IMultiStepFormProps } from './types';
import './i18n';

declare global {
  interface Window {
    pushFormValue: Function;
    removeFormValue: Function;
    setFormValue: Function;
  }
}

const MultiStepForm = <T,>(props: IMultiStepFormProps<T>) => {
  const {
    children,
    initialValues = {},
    onSubmit,
    onValuesChange,
    tabs = [],
    tabsEnabled = false,
  } = props;
  const [page, setPage] = useState<number>(0);
  const [values, setValues] = useState<T>(initialValues as T);
  const pages = Children.toArray(children).filter(isValidElement);

  useEffect(() => {
    setValues(initialValues as T);
  }, [initialValues]);

  const next = (data: T) => {
    setPage((prevPage) => Math.min(prevPage + 1, pages.length - 1));
    setValues(data);
  };

  const previous = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const validate = (data: Record<string, unknown>) => {
    const activePage = pages[page] as ReactElement;
    return activePage.props.validate ? activePage.props.validate(data) : {};
  };

  const onFormSubmit = (data: Record<string, unknown>) => {
    const isLastPage = page === pages.length - 1;
    if (isLastPage) {
      return onSubmit(data as T);
    }
    return next(data as T);
  };

  const activePage = pages[page] as ReactElement;
  const isLastPage = page === pages.length - 1;

  return (
    <Form
      initialValues={values as Record<string, unknown>}
      mutators={{
        ...arrayMutators,
        setValue: ([field, value], state, { changeValue }) => {
          changeValue(state, field, () => value);
        },
      }}
      onSubmit={onFormSubmit}
      validate={validate}
    >
      {({
        form: {
          mutators: { push, remove, setValue },
        },
        handleSubmit,
        submitting,
      }: FormRenderProps) => {
        if (!window.pushFormValue) window.pushFormValue = push;
        if (!window.removeFormValue) window.removeFormValue = remove;
        if (!window.setFormValue) window.setFormValue = setValue;
        return (
          <FormContent onSubmit={handleSubmit}>
            {onValuesChange && (
              <FormSpy subscription={{ values: true }}>
                {(params) => {
                  onValuesChange(params.values as T);
                  return null;
                }}
              </FormSpy>
            )}
            {tabs.length > 0 && (
              <>
                <Tabs
                  onChange={(_: SyntheticEvent, newValue: number) => setPage(newValue)}
                  scrollButtons="auto"
                  value={page}
                  variant="scrollable"
                >
                  {tabs.map((tab, i) => (
                    <Tab key={`tab-${tab}`} label={tab} disabled={!tabsEnabled && page < i} />
                  ))}
                </Tabs>
                <br />
              </>
            )}
            {activePage}
            <FormButtonContainer>
              <FormSubmitButton disabled={submitting} type="submit">
                {i18next.t<string>(
                  isLastPage ? 'MULTI_STEP_FORM:SUBMIT_BUTTON' : 'MULTI_STEP_FORM:NEXT_BUTTON',
                )}
              </FormSubmitButton>
              {page > 0 && (
                <>
                  <br />
                  <FormSubmitButton disabled={submitting} onClick={previous} type="button">
                    {i18next.t<string>('MULTI_STEP_FORM:PREVIOUS_BUTTON')}
                  </FormSubmitButton>
                </>
              )}
            </FormButtonContainer>
          </FormContent>
        );
      }}
    </Form>
  );
};

// eslint-disable-next-line react/jsx-no-useless-fragment
MultiStepForm.Page = ({ children }: { children: ReactNode }) => <>{children}</>;

export default MultiStepForm;
